// Book trade — single-rail picker per side (asset + treasury picked together).
//
// Structural fix (2026-05-22): asset and treasury slug are not two decisions,
// they are ONE. The operator picks a "rail" (a real desk wallet holding a
// specific asset). The asset is implied by that pick; payment_method is
// derived from the rail's kind.
//
// 14 Sept 2026 (SWEEP-SCOPE-2026-09-13 §4.3, §8 items 14 15 16 21 24 25 27
// 40): the guards this screen grew that day are WORDS, DEFAULTS AND CONFIRMS
// — never an automatic swap of sides, never a direction that follows a coin
// (both tried and withdrawn on 12 Sept). Every one of them reads the same
// state the payload is built from, so what the sentence says is what the
// books get.
//
// 14 Sept 2026, the minimalist pass (SWEEP-SCOPE §5.1, §6, §7, item 41):
// words moved, boxes folded, copies deleted — mechanisms untouched. The
// subtitle, the settle-later helper, the calculator rule and the date rule
// live in "About this screen"; the Trade | Manager's cheque control is a
// header link; Notes sit under More; the order is Party → direction → legs →
// Rate + When → More → Book; the legs' errors sit under the legs, the read
// error above Party, every post-tap panel above the button; the local Field
// is the shared one; the box line, the errors, the button and the success
// line use the §6 words; settle-later opens ticked for a merchant.

import { useState, useMemo, useEffect, useRef } from 'react';
import { ApiError, errorWords, explain, isSentinel, nothingLanded, readWords } from '../lib/errors';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../components/RepeatPanel';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field, FieldRow } from '../components/Field';
import { ErrorLine, WarnLine } from '../components/ErrorLine';
import { AccountsDown, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { boxParts, BoxWords } from '../components/BoxLines';
import { tg } from '../lib/telegram';
import { derivePaymentMethod, DEFAULT_RAILS, isFiat, ASSETS } from '../lib/workflow';
import { Combobox, type ComboboxOption, NO_PARTY_CREATE_HINT } from '../components/Combobox';
import { rosterCut } from '../lib/roster-cut';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { AssetPicker } from '../components/AssetPicker';
import { AsOfChips } from '../components/AsOfChips';
import { MoreDetails } from '../components/MoreDetails';
import { RecentEntries } from '../components/RecentEntries';
import { displayAsset, dubaiDate, fmtDate, fmtDay, dubaiWallToIso, isoToDubaiWall, backdateProblem, fmtAmount, fmtRate, shownAmount } from '../lib/format';
import { partyName, partySubtitle, isStale, needsNameReview, sameAsset, partyBoxWords, foldAsset, partyBoxKey, dustFor, railName, deskHolds } from '../lib/naming';
import { useTradeMath } from '../hooks/useTradeMath';
import { rateProblem } from '../lib/amount';
import { bandWords, bandFor } from '../lib/trade-math';
import { readState } from '../lib/read-state';
import { partyKeyOf, shouldClearSettleLater, conversionGateOf } from '../lib/settle-later';

type Direction = 'customer_buy' | 'customer_sell';
type Entity = 'dubaiotc' | 'indiaotc';

/** The party's box in ONLY the assets on this trade, in the §6 words —
 *  "owes you 52,728 AED · you hold 67,167 EUR", or "square". Operator, 12
 *  Sept 2026: the general three-biggest line hid the EUR he was about to
 *  trade behind INR, AED and USDT he was not. The words are BoxLines' (one
 *  vocabulary, one dust rule, 14 Sept 2026); a square asset adds nothing.
 *  The figures are the answering ones (emphasis rule, styles.css). */
function TradedBox({ balances, assets }: { balances: Record<string, number> | undefined; assets: string[] }) {
  const bal = balances ?? {};
  const seen = new Set<string>();
  // A stablecoin reads the party's one netted dollar line (partyBoxKey), named in the trade's own coin.
  const parts = assets
    .map((a) => ({ key: partyBoxKey(a), label: foldAsset(a) }))
    .filter(({ key }) => key && !seen.has(key) && seen.add(key))
    .map(({ key, label }) => ({ a: label, v: Object.entries(bal).reduce((sum, [k, x]) => sum + (partyBoxKey(k) === key ? Number(x) : 0), 0) }))
    .filter(({ a, v }) => boxParts(v, a).figure !== null);
  if (parts.length === 0) return <>square</>;
  return (
    <>
      {parts.map(({ a, v }, i) => (
        // One leg is one value: "you hold 9,876,543.21 USDT" never leaves its unit on the next line (22 Oct 2026 review).
        <span key={a}>{i > 0 && ' · '}<span className="whitespace-nowrap"><BoxWords value={v} asset={a} answer /> {a}</span></span>
      ))}
    </>
  );
}

/** One leg of a trade as ONE value: the amount and its unit joined by a no-break space, so a sentence wraps between
 *  complete values and never strands "USDT·TRC" on a line of its own. */
const legWords = (leg: { asset: string; amount: number }) => `${fmtAmount(leg.amount, leg.asset)}\u00a0${displayAsset(leg.asset)}`;

/** The trade in words, once both legs are known: the name, then each leg an unbreakable "amount unit". */
export function TradeReadback({ who, nameKnown, take, give }: {
  who: string; nameKnown: boolean; take: { asset: string; amount: number }; give: { asset: string; amount: number };
}) {
  return (
    <p className="text-body text-tg-text">
      <span className={nameKnown ? 'who' : 'text-tg-hint'}>{who}</span> pays{' '}
      <span className="amt-key whitespace-nowrap">{fmtAmount(take.amount, take.asset)} {displayAsset(take.asset)}</span> and receives{' '}
      <span className="amt-key whitespace-nowrap">{fmtAmount(give.amount, give.asset)} {displayAsset(give.asset)}</span>
    </p>
  );
}

/** What a booking said, read from what was POSTED (never the live fields, which clear on success). */
export interface Booked {
  asOf: string | null; dealId: string; deduped: boolean;
  /** The party's name and the two legs, each "amount unit" as one value. */
  who: string; pays: string; gets: string;
  /** Who the trade was with and whether INR moved on either leg — what the two
   *  links under the success line need (read from what was POSTED, like the rest). */
  partySlug: string; inr: boolean;
}

/** The answer to a booking: a headline of what happened (design system §3: one line, at most ten words) and, under it
 *  in muted type, who it was with and the day. A deduped answer says so instead — the books already held this id and
 *  kept the first date. */
export function BookedLine({ booked }: { booked: Booked }) {
  if (booked.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/trades" className="btn-link text-meta">Trades →</Link>}>
        Already booked as <span className="font-mono">{booked.dealId}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={booked.dealId} />}>
        ✓ Booked — pays <span className="whitespace-nowrap">{booked.pays}</span>, receives <span className="whitespace-nowrap">{booked.gets}</span>
      </StateLine>
      <p className="text-meta text-tg-hint">
        <span className="who">{booked.who}</span>{booked.asOf ? ` · as of ${fmtDate(booked.asOf)}` : ''}
      </p>
    </div>
  );
}

/** A label hint from its parts — "AED per USDT · auto", "from chain" — or
 *  nothing when no part applies, so the label stays bare. */
function hintWords(...parts: (string | false | undefined)[]): string | undefined {
  return parts.filter(Boolean).join(' · ') || undefined;
}

export function NewTrade() {
  const nav = useNavigate();

  // One desk. The entity state and the `me` query that once fed a desk switch
  // were dead — no switch is rendered (§5.1, 14 Sept 2026).
  const entity: Entity = 'dubaiotc';
  const [search] = useSearchParams();
  // Arriving from the Migrate checklist with a legacy row in hand.
  //
  // The screen used to send the operator away to book, then back to type the
  // deal id from memory — which is where "DEAL BOOKED" came from. Now the row
  // carries its own facts across: who, which way, and the two AMOUNTS.
  //
  // Amounts and not the rate, deliberately: legacy's displayed rate is rounded
  // and re-deriving a total from it produced 2,116,623.60 against a true
  // 2,116,624.00. The rate box fills itself from the two amounts.
  //
  // What is NOT prefilled is the two rails. Which pot the money came from is the
  // operator's judgment, and the currency guard catches a wrong one — whereas a
  // guessed rail would look authoritative and be silently wrong.
  const [direction, setDirection] = useState<Direction>(
    search.get('dir') === 'customer_sell' ? 'customer_sell' : 'customer_buy');
  // THE TOGGLE OPENS ON THE LAST DIRECTION THE DESK BOOKED WITH THIS PARTY
  // (operator's answer, 14 Sept 2026, to sweep item 14 — not "unpicked"). It
  // is a default, not a rule: the moment the operator touches the toggle, or
  // a link arrives with ?dir= (a chain row, a party page), the party's history
  // is not consulted again for this visit. The readback line above Book says
  // the shape in words either way.
  const dirTouched = useRef(search.has('dir'));
  const pickDirection = (d: Direction) => { dirTouched.current = true; setDirection(d); };
  // ?party=<slug> from a party page or the Migrate checklist.
  const [partySlug, setPartySlug] = useState(search.get('party') ?? '');
  // THE PARTY'S IDENTITY, as the books read it: the BFF trims and lowercases party_slug before it looks the party up (routes/trades.ts), so
  // "hartley-trading ", "HARTLEY-TRADING" and "hartley-trading" are ONE party, booked to one account. Every comparison, query key and read
  // below goes through this — never the raw text of the box, which keeps what was typed (a guard keyed to the raw text lets a space or a
  // capital letter walk round it).
  const partyKey = partyKeyOf(partySlug);

  // Each side is ONE choice (rail = treasury slug + asset together).
  // Initial defaults reflect the 95% case: customer brings AED cash, takes USDT-TRC.
  // When operator flips direction below, both rails swap automatically.
  // Arriving from a Chain row (?rail=&railAsset=): the wallet that moved IS
  // the crypto side of this trade — desk buys → coins arrived (take side of a
  // customer_sell), desk sells → coins left (give side of a customer_buy).
  const chainRail: RailValue | null =
    search.get('rail') && search.get('railAsset')
      ? { slug: search.get('rail')!, asset: search.get('railAsset')! } : null;
  // Without a chain rail the defaults already follow ?dir= on the FIRST render
  // (12 Sept 2026). The direction effect below sets the same rails a frame
  // later anyway; but the calculator seats a prefilled ?take= / ?give= amount
  // by ASSET, so the box it lands in must be the box the asset ends up in —
  // not the pre-swap default it would have had for one frame.
  const urlSell = search.get('dir') === 'customer_sell';
  // WHICH SIDE THE URL'S RAIL BELONGS ON. A chain row says nothing but its
  // direction, and the guess from that — the wallet is the coin side, so the
  // take side of a sell and the give side of a buy — was right for every
  // wallet and wrong for every currency, which is why Record money's links
  // could only carry a coin's rail (14 Sept 2026, sweep items 29 and 39).
  // ?railSide=take|give (Record money sends it: take when the party paid the
  // desk, give when the desk paid the party) names the side outright; absent,
  // the direction-derived guess stands, so every chain link reads as before.
  const railSideParam = search.get('railSide');
  const railOnTake = chainRail !== null
    && (railSideParam === 'take' || railSideParam === 'give' ? railSideParam === 'take' : urlSell);
  // ?takeAsset / ?giveAsset (the deal panel's "Book it again the other way"
  // link, 14 Sept 2026) name the assets of a settle-later rebook outright.
  const urlTakeAsset = search.get('takeAsset'), urlGiveAsset = search.get('giveAsset');
  const [takeRail, setTakeRail] = useState<RailValue>(
    chainRail && railOnTake ? chainRail
      : urlTakeAsset ? { slug: '', asset: urlTakeAsset }
      : { ...(urlSell ? DEFAULT_RAILS.hotTrc : DEFAULT_RAILS.cash) });
  // The OTHER side of a chain-row booking takes the direction's own default —
  // the money side is cash, the coin side the hot wallet. Before 12 Sept 2026
  // a coins-IN row (customer sells) put the wallet on the take side and left
  // the give side on its usual hot-wallet default, so the screen opened with
  // Hot wallet · TRC on BOTH sides and a same-account error.
  const [giveRail, setGiveRail] = useState<RailValue>(
    chainRail && !railOnTake ? chainRail
      : urlGiveAsset ? { slug: '', asset: urlGiveAsset }
      : { ...(urlSell ? DEFAULT_RAILS.cash : DEFAULT_RAILS.hotTrc) });
  // ?lock=coin (a Chain row's booking, sweep item 26, 14 Sept 2026): the coin
  // box shows what the chain moved and cannot be typed over; total and rate
  // derive from it. The chain is the fact; the trade is booked to it.
  const lockCoin = search.get('lock') === 'coin' && chainRail !== null && !isFiat(chainRail.asset);
  const lockedSide: 'take' | 'give' | null = lockCoin ? (railOnTake ? 'take' : 'give') : null;
  // THE CHAIN ROW'S OWN DIRECTION (sweep item 16, 14 Sept 2026): coins IN to
  // the wallet is a customer selling, coins OUT a customer buying. The toggle
  // may still be flipped — nothing here flips it back — but the amber line
  // below the toggle says what the chain row is while it disagrees. A
  // currency rail from a Record money link is not a chain row, so it says
  // nothing about coins in or out.
  const chainDir: Direction | null = chainRail && !isFiat(chainRail.asset)
    ? (railOnTake ? 'customer_sell' : 'customer_buy') : null;

  // THE THREE-WAY CALCULATOR. Operator, 12 Sept 2026: "screens everywhere for
  // asset-1, asset-2 & rate and 3-way auto calculation, that too dynamic which
  // changes with each number I change in the 2nd box" — and "even if I
  // directly paste the rates, total should be there". The shared hook owns the
  // two amounts and the rate: any two fill the third on every keystroke or
  // paste, the rate reads quote per base by the PAIR (AED per USDT, INR per
  // AED, AED per EUR) whichever way the trade goes, an amount outranks the
  // rate (correcting a total re-derives the rate, never the coins — operator,
  // 11 Sept), and a rail switch drops the rate (the 2026-08-22 case: an
  // AED-era 0.0100 survived a switch to the Angadiya INR rail and turned a
  // 498 USDT purchase into "4.98 INR"). Rules in lib/trade-math.ts; nothing
  // here re-implements them.
  const math = useTradeMath(takeRail.asset, giveRail.asset);
  // Read-only views for validation, the payload and the Book button. The
  // boxes themselves read and write the hook directly.
  const amountTake = math.amountOf(takeRail.asset);
  const amountGive = math.amountOf(giveRail.asset);
  const rate = math.rate;
  // ?take= / ?give= (a Chain row's coins) go in as if typed FIRST, so a rate
  // pasted afterwards moves the other box and the coins stay (operator,
  // 1 Sept — "amount × rate = total, any 2 = autofill 3rd"). Mount only: the
  // URL does not change while the screen is open.
  useEffect(() => {
    const give = search.get('give'), take = search.get('take');
    if (give) math.preset({ asset: giveRail.asset }, give);
    if (take) math.preset({ asset: takeRail.asset }, take);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const [notes, setNotes] = useState(search.get('note') ?? '');
  // Which amount box has the cursor — a box the calculator filled shows its figure grouped, four places at most
  // (shownAmount, display only); the one being edited shows the raw string, so a formatter never fights the typing.
  const [editing, setEditing] = useState<'take' | 'give' | null>(null);
  // PICKING A SIDE CHANGES THAT SIDE ONLY. Two automatic behaviours were
  // tried and withdrawn on 12 Sept 2026 — the other side snapping to a
  // counter-asset, and the direction (or the rails) following the coin. The
  // operator's ruling, end of day: "revert this to how it was, I am ok with
  // the same asset selection and will change manually." The same-asset error
  // line and the books' own refusal remain the guards.

  // ── SETTLE LATER — MERCHANT ONLY (operator ruling 2026-08-25) ─────────────
  // "Agreed, nothing moved yet" is this desk's normal merchant shape: HK's INR
  // lands on the day and the USDT follows, in tranches, sometimes days later.
  // Legacy proves it is the rule rather than the exception — every merchant
  // carries 2.4x to 9x more money-movements than trades, so no merchant trade
  // was ever a single self-settling row.
  //
  // It is deliberately NOT offered for customers. A walk-in trade that settles
  // on the spot has no use for it, and an accidental tick would leave a
  // customer carrying a position nobody chases. The control does not exist
  // unless the selected party is a merchant.
  const [settleLater, setSettleLater] = useState(search.get('later') === '1');
  // THE SHAPE A DOOR IS ABOUT TO SEND, read in one place for the payload (buildBody) and for the booking gate (`book`): the switch, or the
  // repeat panel's remedy ("Book it settle-later instead") riding in the submit's variables. The switch alone misses the second.
  const laterFor = (choice?: { kind: string }) => settleLater || choice?.kind === 'settle_later';
  // TICKED BY DEFAULT FOR A MERCHANT (§5.1, 14 Sept 2026: 57 of 72 merchant
  // trades were settle-later, 2 of 49 customer trades). A default, like the
  // direction: applied once per party pick, never after the operator has
  // touched the switch or a link carried ?later=.
  // A CHAIN RAIL ON THE URL DECLARES THE SHAPE TOO (review, 14 Sept 2026):
  // a Chain row's link (?rail=&railAsset=&lock=coin, no ?party, no ?later)
  // says coins moved through that wallet, so the trade is booked settled to
  // it. Before this line the merchant default fired when the operator picked
  // a merchant, the rails effect then dropped the wallet's slug and the
  // payload went out unsettled with no treasury leg — the chain row could
  // not match it, and unticking landed on the TRC default, not the wallet
  // (the 3 Sept ERC-row-on-TRC path). Record money's links send ?later=1
  // alongside the rail, so they read exactly as before.
  const laterTouched = useRef(search.has('later') || chainRail !== null);
  const laterDefaultedFor = useRef<string | null>(null);

  // ── AS OF — WHEN DID THIS HAPPEN? (5 Sept 2026) ───────────────────────────
  // Loki OTC's 3 Sept trade (62,449 USDT at 3.675 for 229,500 AED) was never
  // booked: both money legs were recorded that day, but this screen could only
  // say "now", so the trade would have landed on the 5th — which the sweep
  // cannot settle from 3 Sept money — or been booked settled and double-booked
  // the money. Empty means now (every walk-in). Set, it is the instant the
  // books record, for BOTH shapes. The value is Dubai wall-clock, read through
  // lib/format.ts: a datetime-local carries no zone, and the books have one.
  // Since 14 Sept 2026 the choice is explicit — Now · Yesterday · Pick a time
  // (components/AsOfChips.tsx) — so an omitted date is a chip the operator
  // saw, not a box they skipped (sweep item 23).
  const [when, setWhen] = useState<string>(search.get('when') ?? '');   // ?when=2026-09-03T18:03 prefills the instant (Dubai wall-clock), like ?take/?give
  // The time a Chain row (or a Record money link) sent along: shown as its own chip.
  const urlWhen = search.get('when') ?? '';
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // WHAT THE LAST BOOKING SAID, kept apart from the live field (review, 5 Sept
  // 2026). The success line read `whenIso`, and nothing cleared `when` on
  // success — so after Loki's 3 Sept trade a walk-in typed straight after went
  // out dated 3 Sept as well, and the sweep pooled 3 Sept money for it. The
  // date now leaves with the trade it was typed for (onSuccess), and this
  // holds what was actually POSTED: the instant, the id, the trade in words
  // (the figures clear on success, so the line cannot read the boxes), and
  // whether the BFF deduped it — a retry whose first attempt had landed keeps
  // that attempt's date, whatever this request said.
  const [booked, setBooked] = useState<Booked | null>(null);

  // Data — fetch a base list of recent customers/merchants on mount so the
  // dropdown is populated the moment the operator focuses the input. As they
  // type, the BFF re-queries with the search term.
  const parties = useQuery({
    queryKey: ['parties', entity, partySlug.trim()],
    queryFn:  () => api.listParties({ entity, q: partySlug.trim() || undefined, with_balances: true }),
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  // WHAT EACH READ IS WORTH — `readState`, said in ONE place (lib/read-state.ts; design system Appendix B): 'known' (a read that
  // SUCCEEDED), 'waiting' (nothing yet: a first read, the retry of a failure, an offline pause) or 'failed' (an error — a failed REFRESH
  // keeps its old rows beside it). A guard needs 'known'; a banner says 'failed' with its Try again and 'waiting' with a short reading
  // line; nothing is claimed from either (no "square", no "no such party", no clear to book).
  // The typed name's search: until it is RESOLVED nobody knows whether this is a party the books hold or a new name.
  const lookup = readState(parties);
  // …and its balances: a list the BFF flags `balances_read: false` has the names and not the balances (its empty balances are not "square").
  const listBalances = readState(parties, { balances: true });
  const accounts = readState(treasury);
  // The picker's box words take the balances' answer.
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry, whatever is typed — the list on screen is one query per search, so the flag is read
  // off the list that is showing, a party picked or not (Astra, 23 Oct 2026: the words went and nothing said why).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  // Customer Buys / Sells = trades WITH a customer or peer counterparty —
  // own_account treasuries must NOT appear in this autocomplete.
  const partyOptions: ComboboxOption[] = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account')
      .slice(0, 20)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        // who they are AND what stands between you — the identifier that
        // matters at a money desk (operator, 2 Sept)
        secondary: [partySubtitle(p, { compact: true }), listBalancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, listBalancesRead]);

  // Existing balance for the selected party — only fetch when the typed slug
  // matches a known party (skips during lazy-create flow when operator is
  // typing a brand-new name).
  const partyExists = useMemo(
    () => (parties.data?.parties ?? []).some((p) => p.slug === partyKey),
    [parties.data, partyKey],
  );
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === partyKey) ?? null,
    [parties.data, partyKey],
  );
  // THE NEAREST EXISTING NAMES (§5.1, 14 Sept 2026): what the books found for
  // the typed text, offered as "Did you mean …?" before the create-as buttons
  // — a duplicate party is one careless tap otherwise.
  const nearest = useMemo(
    () => partyOptions.filter((o) => o.value !== partyKey).slice(0, 3),
    [partyOptions, partyKey],
  );

  // THE PARTY'S RAILS AND LAST DIRECTION, in the one round trip RailPicker
  // already makes (same query key, so react-query asks once and both read
  // the answer). `last_direction` is the latest non-voided deal with this
  // party; a party with no trade yet opens on Customer buys.
  const partyRails = useQuery({
    queryKey: ['party', partyKey, 'rails', entity],
    queryFn:  () => api.partyRails(partyKey, entity),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // ONCE PER PARTY PICK, not on every refetch: the rails answer refreshes on
  // window focus and after each posting, and re-applying the default then
  // would move a toggle the operator had set (review, 14 Sept 2026).
  const dirDefaultedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!partyRails.data || dirTouched.current) return;
    if (dirDefaultedFor.current === partyKey) return;
    dirDefaultedFor.current = partyKey;
    setDirection(partyRails.data.last_direction ?? 'customer_buy');
  }, [partyRails.data, partyKey]);

  // A party's kind cannot change while this form is open, so every merchant we
  // have seen is remembered. Without that the flag would blink: the parties
  // query is re-keyed on the search text, so `data` is undefined for the length
  // of each refetch — long enough to hide the toggle and silently clear it
  // under an operator who had already ticked it.
  const merchantSlugs = useRef<Set<string>>(new Set());
  const isMerchant = useMemo(() => {
    for (const p of parties.data?.parties ?? []) {
      if (p.counterparty_role === 'merchant' || p.party_type === 'counterparty') {
        merchantSlugs.current.add(p.slug);
      }
    }
    return !!partyKey && merchantSlugs.current.has(partyKey);
  }, [parties.data, partyKey]);

  // The settle-later default, once the party is KNOWN (partyExists reads the
  // same answer isMerchant does, so the two agree): a merchant opens ticked,
  // a customer unticked. Skipped for this visit once the switch was touched.
  useEffect(() => {
    if (!partyExists || laterTouched.current) return;
    if (laterDefaultedFor.current === partyKey) return;
    laterDefaultedFor.current = partyKey;
    setSettleLater(isMerchant);
  }, [partyExists, isMerchant, partyKey]);

  const partyBalances = useQuery({
    queryKey: ['party', partyKey, 'balances'],
    queryFn:  () => api.partyDetail(partyKey, entity),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // THE PARTY'S BOX is KNOWN only once a read has SUCCEEDED. A read that failed keeps its old balances beside the error (a failed
  // REFRESH), and a retry of a first failure is PENDING with nothing at all — neither is "square" and neither is what it last said; a
  // paused (offline) read is the same. Every line drawn FROM the box — the box line under the party, the conversion check below — takes
  // this one answer: the figures only when 'known'.
  const box = readState(partyBalances);
  const boxBalances = box === 'known' ? partyBalances.data?.balances : undefined;
  // The ONE reader of the last-known box that stays: who may settle later is a permission, not a line — the switch is not a claim,
  // and going dark on a failed refresh would untick it under the operator and turn a settle-later trade into a settled one.
  const balanceEntries = useMemo(() => {
    const map = partyBalances.data?.balances ?? {};
    return Object.entries(map).filter(([, v]) => v !== 0);
  }, [partyBalances.data]);

  // WHO MAY SETTLE LATER — merchants, and anyone already carrying a position.
  //
  // The rule used to be merchants only, on the reasoning that a walk-in settles
  // on the spot. That is true of a walk-in and false of the books: 45 customers
  // carry running balances, and Kevin — a customer — holds an open unsettled
  // trade booked through ingestion, which never passed this gate. So the console
  // was asserting something the ledger had already contradicted.
  //
  // Operator ruling 2026-08-28: relax it for a customer who already has an open
  // balance. The real distinction was never customer-versus-merchant, it is
  // whether this party carries a position — and a party with a non-zero balance
  // demonstrably does. A party at zero still cannot, which keeps the guard that
  // matters: an ordinary walk-in cannot accidentally book a trade that moves
  // nothing.
  const hasOpenPosition = balanceEntries.length > 0;
  const canSettleLater = isMerchant || hasOpenPosition;
  // Only once the party is KNOWN: on mount the parties list is still loading
  // and this used to switch settle-later off under a ?later=1 link before
  // the merchant had been seen (review, 14 Sept 2026). And only on a KNOWN
  // ineligibility (shouldClearSettleLater): while the box is still being read
  // `hasOpenPosition` is false for a customer who carries a position, and
  // clearing the switch then turned the trade into a settled one — rails
  // restored, the conversion guard walked round — before the box could say
  // (Astra, 23 Oct 2026). While either read waits or failed the switch stays
  // as the operator, or the link, set it.
  useEffect(() => {
    if (shouldClearSettleLater({ partyExists, lookup, box, canSettleLater })) setSettleLater(false);
  }, [partyExists, lookup, box, canSettleLater]);

  // Direction toggle swaps rails to the 95% defaults for that direction.
  // customer_buy : take = cash AED  (customer pays)   · give = hot TRC USDT (desk gives)
  // customer_sell: take = hot TRC USDT (customer pays)· give = cash AED     (desk gives)
  //
  // `settleLater` is a dependency for the same reason direction is: it changes
  // what a leg MEANS. Unsettled, only the asset survives — there is no drawer
  // to name — and turning it back off restores the real default rails, so a
  // cleared slug can never be left behind for the payload to inherit.
  // A CHAIN CHIP'S WALLET MUST SURVIVE THE FIRST RENDER (operator, 3 Sept:
  // booked an 18,345 USDT·ERC sale from the ERC row and it landed on the TRC
  // wallet). This effect also fires on mount, and it overwrote the wallet the
  // chip had just put in the URL with the TRC default — invisible on TRC rows
  // because the default IS TRC. It now skips its first run when a rail came
  // from the URL; a later direction flip still swaps to the defaults.
  const railsFromUrl = useRef(chainRail !== null || !!urlTakeAsset || !!urlGiveAsset);
  const lastDirection = useRef(direction);
  useEffect(() => {
    if (railsFromUrl.current) { railsFromUrl.current = false; lastDirection.current = direction; return; }
    const d = direction === 'customer_buy'
      ? { take: DEFAULT_RAILS.cash,   give: DEFAULT_RAILS.hotTrc }
      : { take: DEFAULT_RAILS.hotTrc, give: DEFAULT_RAILS.cash   };
    const flipped = lastDirection.current !== direction;
    lastDirection.current = direction;
    if (flipped) {
      // A direction flip swaps to that direction's defaults, as always.
      setTakeRail(settleLater ? { slug: '', asset: d.take.asset } : { ...d.take });
      setGiveRail(settleLater ? { slug: '', asset: d.give.asset } : { ...d.give });
      return;
    }
    // A settle-later toggle alone keeps the ASSETS the operator picked (an
    // EUR/USDT pair stayed EUR/USDT — review, 14 Sept 2026): on, the drawers
    // go; off, each side takes its default drawer for the asset it holds,
    // else the direction's default.
    setTakeRail((r) => settleLater ? { slug: '', asset: r.asset || d.take.asset } : { ...d.take });
    setGiveRail((r) => settleLater ? { slug: '', asset: r.asset || d.give.asset } : { ...d.give });
  }, [direction, settleLater]);

  // ── VALIDATION — words before the round trip, from the payload's own state ─
  // The §6 words (item 72): "Pays and receives can't be the same account /
  // asset" — the legs are named on screen as pays / gets, never take / give.
  const sameRail = takeRail.slug === giveRail.slug && sameAsset(takeRail.asset, giveRail.asset) && takeRail.slug !== '';
  const sameAssetPair = !!takeRail.asset && sameAsset(takeRail.asset, giveRail.asset);
  const legsProblem = sameRail ? "Pays and receives can't be the same account."
    : sameAssetPair ? "Pays and receives can't be the same asset." : null;
  const pairKnown = !!takeRail.asset && !!giveRail.asset && !sameAssetPair;
  const takeN = Number(amountTake) || 0, giveN = Number(amountGive) || 0, rateN = Number(rate) || 0;

  // THE RATE BOX (sweep item 21): the calculator cleans a pasted "3,67" or
  // "AED 3.672" on the way in; whatever is still not a plain decimal is named
  // here and Book stays off — the same sentence the BFF answers with, one
  // voice (lib/amount.ts, lib/errors.ts rateWords).
  const rateShape = rateProblem(rate);

  // SIDES SWAPPED (sweep item 40): a currency on the coin side and a coin on
  // the money side — the shape the BFF refuses as `sides_swapped` (Loki's EUR
  // conversion, J639D, 11 Sept). Said here, in the BFF's own words via
  // lib/errors.ts explain(), before any request; Book stays off. NOTHING is
  // flipped for the operator: they pick.
  const swapped = useMemo(() => {
    if (!pairKnown) return null;
    const coinSide  = direction === 'customer_buy' ? giveRail.asset : takeRail.asset;   // what the customer buys or sells
    const moneySide = direction === 'customer_buy' ? takeRail.asset : giveRail.asset;   // what it is priced in
    if (!isFiat(coinSide) || isFiat(moneySide)) return null;
    return explain(400, { error: 'sides_swapped', fiat: displayAsset(coinSide), coin: displayAsset(moneySide) });
  }, [pairKnown, direction, takeRail.asset, giveRail.asset]);

  // THE RATE BAND (sweep item 24), one table for every screen (lib/trade-math
  // .ts BAND, keyed by the oriented pair). A TYPED off-band rate turns Book
  // off until the operator presses the confirm that names the consequence;
  // a rate the calculator DERIVED from two typed amounts is only amber — the
  // amounts are the facts, and an odd rate between them is a question, not
  // a slip in the rate box.
  const bandLine = pairKnown && rateN > 0 && !rateShape ? bandWords(math.base, math.quote, rateN) : null;
  const bandTyped = !!bandLine && !math.rateIsDerived;
  // The confirm remembers WHICH rate it answered: retype the rate and it is
  // asked again.
  const [bandAck, setBandAck] = useState('');
  const bandPending = bandTyped && bandAck !== rate;

  // THE DESK'S OWN DEALT MEDIAN (sweep item 25): one line when this rate is
  // more than 1 % from what the desk dealt on this pair, this way, in the
  // last 24 hours. Book stays allowed — a rate can move; the line is there so
  // a 3.26 typed for 3.62 is seen before it is booked.
  const median = useQuery({
    queryKey: ['trades', 'median', entity, math.base, math.quote, direction],
    queryFn:  () => api.tradeMedian({ entity, base: math.base, quote: math.quote, direction }),
    enabled:  pairKnown && rateN > 0,
    staleTime: 60_000,
  });
  const medianRead = readState(median);
  const medianLine = useMemo(() => {
    const m = medianRead === 'known' ? median.data : undefined;   // a failed refresh keeps the old median beside the error, a retry has none: no claim from either
    if (!m || m.median === null || !(rateN > 0) || rateShape || !pairKnown) return null;
    if (Math.abs(rateN - m.median) / m.median <= 0.01) return null;
    // The median to four places — the one fmtRate, on a rounded figure. The pair and the way are the Rate label and
    // the Buys / Sells toggle above it, so the line says only what they cannot: the desk's figure and this one.
    return `desk average today ${fmtRate(Number(m.median.toFixed(4)), math.quote)} (${m.trades} trade${m.trades === 1 ? '' : 's'}) — this one is ${fmtRate(rateN)}`;
  }, [median.data, medianRead, rateN, rateShape, pairKnown, direction, math.base, math.quote]);

  // THE RATE READ BOTH WAYS (21 Oct 2026 audit) is the check for a rate typed
  // upside down, so it is shown wherever that check is still needed: a rate
  // outside its band, a pair the band table does not know (BTC, an unknown
  // coin), or a rate the desk's median flags. A rate plainly INSIDE its band is
  // the right way up by definition (the table is keyed by the oriented pair),
  // and the box's own label says which way it reads.
  const rateInBand = pairKnown && rateN > 0 && !rateShape && !!bandFor(math.base, math.quote) && !bandLine;
  const bothWays = pairKnown && rateN > 0 && !rateShape && (!rateInBand || !!medianLine);

  // THIS IS A CONVERSION (sweep item 27): settle-later ticked and the party's
  // box already holds what they would pay — the desk holds their EUR and the
  // trade has them "paying" EUR. The sweep would net it, which is exactly what
  // Convert balance does on purpose (U0RKO, JP, 11 Sept). Sign law: a
  // negative box is the desk holding theirs.
  const heldTake = useMemo(() => deskHolds(boxBalances, takeRail.asset), [boxBalances, takeRail.asset]);
  // Answered for THIS party, asset, amount and shape; any change asks again.
  const convSigFor = (later: boolean) => `${partyKey}|${takeRail.asset}|${amountTake}|${later ? 1 : 0}`;
  const convSig = convSigFor(settleLater);
  const [convAck, setConvAck] = useState('');
  // THE CONVERSION CHECK, for a trade of the given shape (lib/settle-later.ts conversionGateOf — one statement, read here and by the tests):
  // nothing in the way (null) — or the box already holds what they would pay and nobody has answered ('ask') — or something it stands on
  // has not been SUCCESSFULLY read ('unchecked'), the lookup first (a pending or failed lookup is not "a new name") and then the box.
  // `answered`: this very press is the answer to the question.
  const conversionGate = (later: boolean, answered = false): 'ask' | 'unchecked' | null => conversionGateOf({
    later, partyKey, takeN, lookup, partyExists, box, heldTake, dust: dustFor(takeRail.asset), answered, acknowledged: convAck === convSigFor(later),
  });
  const convGate = conversionGate(settleLater);
  const convPending = convGate === 'ask';
  const conversionUnchecked = convGate === 'unchecked';
  // The button says WHICH read it is standing on (the lookup, then the box) and whether it is still coming or failed.
  const uncheckedWords = lookup !== 'known'
    ? (lookup === 'failed' ? 'Party not read' : 'Reading party…')
    : (box === 'failed' ? 'Balance not read' : 'Reading balance…');

  // ── THE DATING LINE (sweep §4.3 ix / item 23, 15 Sept 2026) ──────────────
  // A settle-later trade dated Now for money that moved yesterday cannot be
  // settled by that money: the sweep draws on movements from a day before
  // the trade's instant onward, never earlier (Loki, 3 Sept). The party's
  // own statement — the same movements the sweep pools (a transfer, a split
  // payment, a receipt, a payment) — is read once the party is known, for
  // the last seven Dubai days, a minute stale. When settle-later is ticked,
  // both amounts are typed and the chips say Now, a live movement older than
  // 12 h that carries exactly one of this trade's legs (asset and amount,
  // either side) is named in one amber line with an offer to date the trade
  // to it. The EARLIEST such movement, deliberately: dated there, every
  // later one is inside the window too. A nudge, not a gate — Book stays
  // enabled; nothing is set without the tap.
  const weekAgo = dubaiDate(Date.now() - 7 * 86_400_000);
  const recentMoney = useQuery({
    queryKey: ['party', partyKey, 'recent-money', entity, weekAgo],
    queryFn:  () => api.partyStatement(partyKey, { entity, from: weekAgo, limit: 500 }),
    enabled:  partyExists,
    staleTime: 60_000,
  });
  const recent = readState(recentMoney);
  // Is the dating check RUNNING (the same conditions it has always had)? When it is and its read failed, the line below says it was not
  // done — a check that could not run is not a check that found nothing.
  const datingWanted = settleLater && partyExists && when === '' && takeN > 0 && giveN > 0;
  const dating = useMemo(() => {
    if (!datingWanted) return null;
    const rows = recent === 'known' ? (recentMoney.data?.rows ?? []) : [];   // a failed refresh keeps the old rows beside the error, a retry has none: no nudge from either
    const now = Date.now();
    const cutoff = now - 12 * 3_600_000;
    const MONEY = new Set(['transfer', 'party_payment_split', 'receipt', 'payment']);
    const legs = [{ asset: takeRail.asset, amount: takeN }, { asset: giveRail.asset, amount: giveN }];
    let pick: { at: string; reference: string | null } | null = null;
    for (const r of rows) {
      if (r.state !== 'live' || !r.at || !MONEY.has(r.kind)) continue;
      const t = Date.parse(r.at);
      if (!Number.isFinite(t) || t > cutoff) continue;
      // Exactly this leg: the statement's delta is the amount as it moved
      // the box, signed; the leg is unsigned. Float noise only.
      if (!legs.some((l) => sameAsset(r.asset, l.asset) && Math.abs(Math.abs(r.delta) - l.amount) < 1e-6)) continue;
      if (backdateProblem(isoToDubaiWall(r.at))) continue;
      if (!pick || t < Date.parse(pick.at)) pick = { at: r.at, reference: r.reference };
    }
    return pick && { ...pick, hours: Math.round((now - Date.parse(pick.at)) / 3_600_000) };
  }, [datingWanted, takeN, giveN, takeRail.asset, giveRail.asset, recentMoney.data, recent]);

  // THE READBACK (sweep item 14) — the trade in words, from the same fields
  // the payload reads, once both amounts exist: "Loki OTC pays 8,091 USDT·TRC
  // and receives 29,694 AED". The legs above carry the same name.
  //
  // THE NAME, NEVER THE SLUG (14 Sept 2026): opened with ?party=<slug> from a
  // party page or a Chain row, the leg labels, the readback and the balance
  // line read "loki-otc pays" until the party was retyped. The display name
  // lands the moment the parties query resolves (partyName on the record);
  // until then the slug shows muted and switches by itself.
  const nameKnown = !!selectedParty;
  const partyWords = selectedParty ? partyName(selectedParty) : partySlug;
  const who = partyWords || 'Customer';
  // THE LEG LABELS NAME ONLY A PARTY THE BOOKS KNOW (21 Oct 2026 audit): while the
  // name was still being typed they read "ha pays / ha gets" — the fragment, not a
  // party. Until the typed text IS a known party the labels use the screen's own
  // neutral word. The readback and the success line keep `who`: by then a name has
  // been typed on purpose (a new party's) and the sentence is the one being booked.
  const legWho = selectedParty ? partyWords : 'Customer';
  const readback = pairKnown && takeN > 0 && giveN > 0 && !!partySlug;
  // THE TOGGLE NAMES THE COIN (§6 trade direction): "Buys USDT / Sells USDT"
  // — the base of the oriented pair is what is bought or sold (USDT in
  // USDT/AED, EUR in EUR/AED, AED in AED/INR), read from the picked accounts.
  const coinWord = pairKnown ? displayAsset(foldAsset(math.base)) : 'USDT';

  // THE CHAIN ROW NUDGE (sweep item 16): the toggle disagrees with the row.
  const chainNudge = chainDir && chainDir !== direction
    ? (chainDir === 'customer_sell'
        ? 'Coins IN to the hot wallet — that is Customer sells.'
        : 'Coins OUT of the hot wallet — that is Customer buys.')
    : null;

  // Map workflow's payment_method ('bank-transfer') to BFF delivery_method ('bank_transfer')
  const PM_TO_DM: Record<string, 'cash' | 'bank_transfer' | 'crypto' | undefined> = {
    cash: 'cash', 'bank-transfer': 'bank_transfer', crypto: 'crypto', other: undefined,
  };

  // Lazy create flow — BFF returns 409 when partySlug doesn't exist, operator
  // picks one of three paths (Tracked / Treasury-only / Merchant — New
  // party's words, item 78), then mutation re-POSTs with that choice.
  type LazyChoice =
    | { kind: 'customer'; profile_kind: 'tracked' | 'treasury_only' }
    | { kind: 'merchant' };
  /** What a submit carries besides the form: an onboarding choice, or the
   *  repeat panel's "Book it settle-later instead" (sweep item 39, 14 Sept
   *  2026). The remedy flips the settle-later switch AND rides in the
   *  variables, because the re-POST it fires reads the closure of the render
   *  it was pressed in — where the switch still says settled. A ref would do
   *  the same job less visibly; the variables also survive a "post it again
   *  anyway" that follows, which re-posts `submit.variables`. */
  type SubmitVars = LazyChoice | { kind: 'settle_later' } | undefined;
  const [pendingProfileChoice, setPendingProfileChoice] = useState<null | { slug: string; display_name: string }>(null);


  // THE DEAL ID IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY.
  //
  // It used to be minted inside buildBody(), which runs on EVERY submit — so a
  // booking that timed out after the BFF had already posted came back as a
  // failure, the operator pressed Book again, and the second attempt carried a
  // NEW deal_id. The ledger dedupes on `reference` and the deals table on
  // (deal_id) DO NOTHING, so a stable id makes the retry a no-op — a fresh one
  // defeats both and books the trade twice. The lazy-party path made it worse:
  // a 409 needs_confirm is a DESIGNED re-POST, and it re-minted the id too.
  //
  // Held in a ref, not state: it must not re-render, and it must not reset when
  // the operator edits a field mid-retry. Cleared on success, so the next trade
  // gets its own.
  const dealIdRef = useRef<string | null>(null);
  // REPEAT GUARD: the twin the books already hold, and the operator's "book
  // it again anyway" — a ref, like the deal id, so it survives the re-POST and
  // clears on success. Keyed by WHICH guard refused (5 Sept 2026): the BFF
  // takes `confirm_repeat` for the twin-trade guard alone and
  // `confirm_money_moved_again` for the cross-shape guard alone, so a yes to
  // one no longer silences the other. A trade can meet both in turn — refused
  // as a twin, confirmed, then refused because its money already moved — and
  // the set keeps both, so the last re-POST carries both flags.
  // The guard, the panel and the confirm are shared with every other posting
  // screen (components/RepeatPanel.tsx). This screen kept its own copy until
  // 3 Sept, which is why the new `money_already_recorded` refusal — the one
  // that catches a settled trade re-recording money already paid — had no panel
  // here, the very screen it fires on. Since 14 Sept the same panel also
  // carries the MIRROR twin (sweep item 15): the BFF sends the sentence in
  // `twin.reason` and the panel prints it as it is.
  // The set also remembers the NEW-RAIL door (14 Sept 2026, evening): "yes,
  // open this asset on this rail" is a third, separate answer, and the BFF
  // asks its doors in turn on every POST, so a re-POST has to carry every
  // yes given so far or an earlier door refuses again (Record money's rule).
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());

  const buildBody = (choice?: SubmitVars) => {
    // The id carries the day the trade HAPPENED — DEAL-2026-09-03-… for a
    // 3 Sept trade typed on the 5th — so it reads and sorts with that day's
    // entries. onWhenChange re-mints it when the date changes (5 Sept 2026).
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    // Settle-later: the switch, or the repeat panel's remedy riding in the
    // variables (see SubmitVars) — the same shape either way.
    const later = laterFor(choice);
    // No rail, no method: an unsettled trade was not delivered by cash or bank
    // or crypto, it was not delivered at all. Guessed here it would go into the
    // books as a delivery that never happened.
    const method = later ? undefined : derivePaymentMethod(takeRail.slug);
    return {
      // The party the guards above checked IS the party the books get: the same normalization the BFF applies (partyKeyOf), sent as the key.
      entity, direction, party_slug: partyKey,
      party_display_name: partySlug,        // operator can edit later via Party Detail
      profile_kind: choice?.kind === 'customer' ? choice.profile_kind : undefined,
      lazy_party_type: choice?.kind === 'merchant' ? 'counterparty' as const : undefined,
      // Two shapes, and the payload says which. A settled trade names both
      // drawers; an unsettled one names neither and carries `unsettled` instead
      // — the BFF REFUSES a rail alongside it rather than ignoring the field,
      // because "nothing moved" and "it moved from here" cannot both be true.
      ...(later
        ? { unsettled: true as const }
        : {
            treasury_take_slug: takeRail.slug,
            treasury_give_slug: giveRail.slug,
            delivery_method: method ? PM_TO_DM[method] : undefined,
            payment_method: method,           // legacy field, kept for back-compat
          }),
      // The amounts and the rate are the calculator's (math.amountOf per rail
      // asset, math.rate) — typed or derived, the books get what is on screen.
      amount_take: { asset: takeRail.asset, amount: Number(amountTake) },
      amount_give: { asset: giveRail.asset, amount: Number(amountGive) },
      rate,
      // payment_method_detail is gone from the payload (§5.1, 14 Sept 2026:
      // never sent in 157 trades — it was always undefined, so the wire is
      // unchanged).
      notes: notes || undefined,
      deal_id: dealId,
      confirm_repeat:            confirmedRef.current.has('repeat') || undefined,
      confirm_money_moved_again: confirmedRef.current.has('money')  || undefined,
      // The operator said yes to opening this asset on this rail (the
      // new-rail panel above the button).
      allow_new_rail_asset:      confirmedRef.current.has('new_rail') || undefined,
      // Only when set. Absent means now; the BFF is never handed "now" as an
      // explicit instant it then has to bound-check against its own clock.
      effective_at: whenIso || undefined,
    };
  };

  // A FAILED READ IS NOT AN EMPTY RESULT. With the pickers empty, the screen
  // offered to create the party the operator had just typed — a duplicate of
  // one that already exists, invisible because the list never loaded. Said
  // above the Party field (§5.1), where the pickers are.
  const readError = lookup === 'failed' ? readWords(parties.error)
    : accounts === 'failed' ? readWords(treasury.error) : null;

  // The failed read says WHICH read, and what not to do on the strength of it (a duplicate party is one careless tap).
  const readWhat = lookup === 'failed' && accounts === 'failed' ? 'parties and accounts' : lookup === 'failed' ? 'parties' : 'accounts';
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): a picker would open on "No such account" and a way to create one.
  const accountsDown = accounts === 'failed' && !treasury.data;
  const retryReads = () => { if (lookup === 'failed') void parties.refetch(); if (accounts === 'failed') void treasury.refetch(); };

  const submit = useMutation({
    mutationFn: async (choice?: SubmitVars) => {
      const body = buildBody(choice);
      const data = await guard(async () => {
        try {
          return await api.bookTrade(body);
        } catch (e) {
          // A new party is this screen's own business; the repeat and
          // already-recorded refusals belong to the shared guard around it.
          const body = e instanceof ApiError && e.status === 409
            ? (e.body as { needs_confirm?: string; suggested?: { slug: string; display_name: string } }) : null;
          if (body?.needs_confirm === 'create_party' && body.suggested) {
            setPendingProfileChoice({ slug: body.suggested.slug, display_name: body.suggested.display_name });
            throw new Error('needs_confirm');
          }
          throw e;
        }
      });
      // WHAT WAS SENT RIDES BACK WITH THE ANSWER, so the success line reports
      // the instant, the id and the figures that were posted — never whatever
      // the fields hold by the time it renders (review, 5 Sept 2026; see
      // `booked`).
      return {
        data, dealId: body.deal_id, asOf: body.effective_at ?? null,
        who, pays: legWords(body.amount_take), gets: legWords(body.amount_give),
        bookedParty: body.party_slug,
        inr: sameAsset(body.amount_take.asset, 'INR') || sameAsset(body.amount_give.asset, 'INR'),
      };
    },
    onSuccess: ({ data, dealId, asOf, who: bookedWho, pays, gets, bookedParty, inr }) => {
      tg()?.HapticFeedback.notificationOccurred('success'); setPendingProfileChoice(null); dealIdRef.current = null;
      clearTwin(); confirmedRef.current.clear();
      // A deduped answer means the books already held this deal id — a first
      // attempt whose response was lost — and kept ITS date; the date in this
      // request was ignored, so it must not be reported as booked.
      setBooked({ asOf, dealId: data.deal_id ?? dealId, who: bookedWho, pays, gets, deduped: !!data.deduped, partySlug: bookedParty, inr });
      // The date leaves with the trade it was typed for. Left in the field, it
      // rode into the next booking — the walk-in after Loki's 3 Sept trade.
      setWhen('');
      // THE FIGURES LEAVE WITH THE TRADE TOO (sweep item 14, 14 Sept 2026): the
      // amounts, the rate, the note and the two confirms clear; the party and
      // the direction stay, so the next trade with the same party is two
      // numbers away and a second tap on the button books nothing. A chain
      // note that stayed would also have tripped the 30-day chain-note twin
      // guard on the next booking.
      math.reset(); setNotes(''); setBandAck(''); setConvAck('');
      // Booked FROM a chain row (?note=chain …): fire the matcher NOW so the
      // pending row is gone before the operator looks back (operator, 2 Sept:
      // "make it auto vanish once trade booked and matched"). Fire-and-forget —
      // a failed sync just leaves the row for the screen's own auto-sync.
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  const rows = treasury.data?.rows ?? [];
  /** The onboarding choice currently being posted, so the pressed button can
   *  say so and the other two can go quiet. */
  const onboarding = submit.isPending ? submit.variables : undefined;

  // A RAIL THAT HAS NEVER HELD THIS ASSET (400 new_rail_asset — sweep item
  // 22, asked of a settled trade's rails since 14 Sept 2026). The books
  // refuse to open an account on their own; the operator opens it in words,
  // from the same panel Record money grew on 12 Sept ("how to?"). The refusal
  // names the asset; the rail is whichever side carries it — both sides
  // carrying it is the same-asset hard stop, so at most one does.
  const newRail = submit.isError && isNewRailRefusal(submit.error)
    ? (() => {
        const asset = String((submit.error as ApiError).body.asset ?? '');
        const rail = sameAsset(asset, giveRail.asset) && !sameAsset(asset, takeRail.asset) ? giveRail : takeRail;
        return { asset: asset || rail.asset, rail, holds: newRailHolds(submit.error) };
      })()
    : null;

  // THE DEAL ID CARRIES THE DAY, so a change of date must re-mint it — or a
  // 3 Sept trade whose date was corrected after a refusal keeps reading
  // DEAL-2026-09-05-…. The ref is kept (see dealIdRef) while the books MAY
  // already hold it: a posting in flight, or a last failure that was not
  // provably answered before the first ledger write. "Any 4xx" was that test
  // until the 5 Sept review showed trades.ts can refuse with a 400/409 AFTER
  // the delivery legs are in the ledger — chart_violation, an inventory
  // refusal, insufficient_funds — and re-minting there is how a trade gets
  // booked twice. lib/errors.ts nothingLanded() names the codes that do come
  // first, and our own sentinels. The confirmations and the twin go with the
  // id: they answered a guard centred on the OLD instant, and a re-POST at
  // the new one must meet the guard afresh.
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; confirmedRef.current.clear(); clearTwin(); }
  };

  // ── THE BUTTON ──────────────────────────────────────────────────────────
  // Hard stops: nothing books until they clear. Confirms (an unusual rate, a
  // conversion) keep Book off until their own button is pressed — a button
  // whose label names the consequence (§7 rule 11).
  const hardStop = submit.isPending || !partySlug || !rate || !amountTake || !amountGive ||
    !takeRail.asset || !giveRail.asset ||
    (!settleLater && (!takeRail.slug || !giveRail.slug)) ||
    sameRail || sameAssetPair || !!pendingProfileChoice ||
    // A back-date the BFF would refuse is refused here first.
    !!whenWords || !!rateShape || !!swapped;
  const bookDisabled = hardStop || bandPending || convPending || conversionUnchecked;
  // MAY THIS DOOR POST? ONE answer for every door that books — the Book button, the rate band's "Book anyway", the conversion's
  // "Continue", the new-account "Yes", the create-party buttons and BOTH repeat-panel doors — read from the shape the door is about
  // to send (laterFor: the switch AND the submit's own choice), never from the button's state alone. A booking whose conversion check
  // is open — asked, or the box unread — does not go out, whichever door it came through. The only `submit.mutate` is below.
  const book = (choice?: SubmitVars, opts: { answered?: boolean } = {}) => {
    if (conversionGate(laterFor(choice), opts.answered)) return;
    submit.mutate(choice);
  };
  const confirmBand = () => { setBandAck(rate); book(undefined); };
  const confirmConversion = () => { setConvAck(convSig); if (!bandPending) book(undefined, { answered: true }); };
  // After a booking the figures are gone and the button says so.
  const bookedAndCleared = submit.isSuccess && !amountTake && !amountGive && !rate;

  const segment = 'flex gap-2 lg:inline-flex lg:w-auto lg:gap-1 lg:p-1 lg:rounded-lg lg:border lg:border-stroke lg:bg-panel';
  // The one toggle (styles.css, emphasis rule): the picked side is lit.
  const segmentButton = (active: boolean) => `seg py-2 lg:py-1.5 ${active ? 'seg-on' : ''}`;

  return (
    <div className="space-y-3 lg:max-w-[720px] lg:mx-auto lg:space-y-4">
      {/* The Manager's cheque door was a header link (§5.1: navigation, not a choice —
          4 cheques against 157 trades). 21 Oct 2026 audit: on a phone that link took a
          row of its own under the title for one small word, so it now sits in About,
          in the sentence that says when a Manager's cheque is the right door. */}
      <ScreenHeader
        title="Book trade"
        help={
          <>
            <p>
              One exchange with one party: what they pay and what they get, each on the account it
              moved through. Both legs settle now. A staged deal — deposit today, seller and delivery
              later — is a{' '}
              <Link to="/mc" className="text-tg-link hover:underline">Manager&rsquo;s cheque</Link>.
            </p>
            <p>
              <strong className="font-medium text-tg-text">Settle later</strong> is for merchants, and for
              anyone already carrying a balance. It books the trade on their account and touches no desk
              account; record the money in or out when it arrives — in one go, in tranches, or days later.
              A merchant opens with it ticked, a customer unticked.
            </p>
            <p>
              <strong className="font-medium text-tg-text">The figures.</strong> Any two of the two amounts
              and the rate fill the third. The box marked auto is the one that moves; type in it and another
              moves instead. The rate reads the pair&rsquo;s way — AED per USDT, INR per AED.
            </p>
            <p>
              <strong className="font-medium text-tg-text">When did this happen?</strong> Now for a walk-in.
              Set it for a trade that happened earlier: the deal id carries that day, and the sweep matches
              it to money recorded around that time. Dubai time.
            </p>
            <p>
              A name the books have not seen is created from the trade, as{' '}
              <strong className="font-medium text-tg-text">Tracked</strong> (KYC required),{' '}
              <strong className="font-medium text-tg-text">Treasury-only</strong> (no KYC; name only) or{' '}
              <strong className="font-medium text-tg-text">Merchant</strong>.
            </p>
            <p>
              The direction opens on the last trade booked with the party — a default, not a rule. The
              sentence above Book reads the trade back before it posts; after a booking the figures clear
              and the party stays.
            </p>
          </>
        }
      />

      {readError && (
        <StateLine
          tone="warn"
          more={readError}
          action={<button type="button" className="btn-link text-meta" onClick={retryReads}>Retry</button>}
        >
          {`Couldn’t read ${readWhat} — ${lookup === 'failed' ? 'don’t create a party yet' : 'try again'}.`}
        </StateLine>
      )}
      {listBalancesFlagged && (
        <StateLine tone="warn" action={<button type="button" className="btn-link text-meta" onClick={() => void parties.refetch()}>Retry</button>}>
          Balances not read — try again.
        </StateLine>
      )}

      <Field label="Party">
        <Combobox
          value={partySlug}
          onChange={setPartySlug}
          options={partyOptions}
          more={rosterCut(parties.data)}
          placeholder="Search party"
          loading={parties.isFetching || lookup === 'waiting'}
          // A list that could not be read is not "no party by that name": it must neither say so nor invite a create — and nor is one still being read.
          emptyHint={lookup === 'failed' ? 'Parties could not be read.' : NO_PARTY_CREATE_HINT}
        />
        {/* THE BOX IN WORDS, NOT IN A COLOUR (§6 party box): the picker above
            reads the box through partyBoxWords; the selected party reads the
            same way, in the assets on this trade. */}
        {partyExists && (box === 'failed' ? (
          // No answer is not "square": the box line is never drawn from a read that failed.
          <StateLine
            tone="warn"
            className="mt-1"
            more={readWords(partyBalances.error)}
            action={<button type="button" className="btn-link text-meta" onClick={() => void partyBalances.refetch()}>Retry</button>}
          >
            Couldn&rsquo;t read the balance — try again.
          </StateLine>
        ) : (
          // …and none is drawn from a read that has not answered either: the first read, a retry of a failure, a paused (offline) one.
          <p className="text-meta text-tg-hint mt-1 px-1">
            {box === 'known'
              ? <TradedBox balances={partyBalances.data?.balances} assets={[takeRail.asset, giveRail.asset]} />
              : 'Reading…'}
          </p>
        ))}
      </Field>

      {/* The direction, customer-side, with the coin named (§6). Settle later
          sits beside it — it changes what the legs below ask for. No "Customer"
          label over it (21 Oct 2026 audit): it sat beside "Party" and was wrong
          for a merchant — "Buys USDT / Sells USDT" says what it is. */}
      <div className="space-y-1 min-w-0">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
          <div className={segment}>
            {(['customer_buy', 'customer_sell'] as const).map((d) => (
              <button key={d} type="button" onClick={() => pickDirection(d)} className={segmentButton(direction === d)}>
                {/* The coin bold, the verb plain (emphasis rule). */}
                {d === 'customer_buy' ? 'Buys' : 'Sells'} <b className="font-semibold">{coinWord}</b>
              </button>
            ))}
          </div>
          {/* Merchants, and anyone already carrying a position. See canSettleLater.
              On the phone (19 Sept 2026 phone pass) the box is thumb-sized on a
              40px row, so the switch is a tap and not a poke; lg+ as before. */}
          {canSettleLater && (
            <label className="inline-flex items-center gap-2 text-body cursor-pointer max-lg:min-h-[40px]">
              <input
                type="checkbox"
                className="max-lg:h-[18px] max-lg:w-[18px]"
                checked={settleLater}
                onChange={(e) => { laterTouched.current = true; setSettleLater(e.target.checked); }}
              />
              Settle later
            </label>
          )}
        </div>
        {/* The chain row's own direction, while the toggle disagrees (item 16).
            Book stays allowed — the operator may know better than the row. */}
        <WarnLine>{chainNudge && `⚠ ${chainNudge}`}</WarnLine>
      </div>

      {/* Customer buys: pays is fiat/cash (customer brings money), gets is crypto.
          Customer sells: pays is crypto (customer brings coins), gets is fiat. */}
      <FieldRow>
        {/* THE BOX THE CALCULATOR FILLS SAYS SO (12 Sept 2026): "auto" on the
            label and muted text in the box, so the operator sees at a glance
            which of the three moves. The hook decides (math.isDerived,
            math.rateIsDerived); the box stays editable — typing in it makes it
            a fact and moves another. A chain row's coins read "from chain". */}
        <Field label={`${legWho} pays`} hint={hintWords(lockedSide === 'take' && 'from chain', math.isDerived(takeRail.asset) && 'auto')}>
          {/* Unsettled: there is no account to name, only an asset. Showing a rail
              picker here would ask a question whose answer is refused by the BFF.
              The desk's everyday three as chips, the rest behind "more…" — the
              operator on the old fourteen-line select, 12 Sept 2026: "Not so
              many, only most used 3". Nothing excluded: the same-asset error
              line below already guards the pair. */}
          {settleLater ? (
            <AssetPicker value={takeRail.asset} onChange={(a) => setTakeRail({ slug: '', asset: a })} options={ASSETS} />
          ) : accountsDown ? (
            <AccountsDown value={takeRail} />
          ) : (
          <RailPicker
            value={takeRail}
            onChange={setTakeRail}
            rows={rows}
            // The customer's own habits rank the list (12 Sept 2026) — once the
            // typed name IS a known party, not while it is still being typed.
            partySlug={partyExists ? partyKey : undefined}
            partyEntity={entity}
            // EVERY TREASURY ACCOUNT, BOTH SIDES, NO AUTOMATION. The per-direction
            // filter (wallets on the pays side of a sell, cash and banks on the
            // other) hid the Wio bank from an operator typing "wio" — "why wio
            // is not here? it's also treasury" (12 Sept 2026, after the auto
            // swapping of sides was withdrawn the same evening). The list is the
            // list; the same-asset line and the books' own refusal are the guards.
            loading={treasury.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder="Pay-in account"
          />
          )}
          <div className="flex gap-2 mt-2">
            {/* Straight through to the calculator, as typed or pasted — no
                coercion, no trimming — so the other boxes move on every keystroke. */}
            <input
              className={`input flex-1 min-w-0${math.isDerived(takeRail.asset) ? ' text-tg-hint' : ''}${lockedSide === 'take' ? ' bg-tg-bg/30' : ''}`}
              value={shownAmount(math.amountOf(takeRail.asset), takeRail.asset, math.isDerived(takeRail.asset), editing === 'take')}
              onChange={(e) => { if (lockedSide !== 'take') math.setAmount(takeRail.asset, e.target.value); }}
              onFocus={() => setEditing('take')}
              onBlur={() => setEditing(null)}
              readOnly={lockedSide === 'take'}
              inputMode="decimal"
              aria-label={`${legWho} pays, amount`}
            />
            <div className="w-24 shrink-0 input bg-tg-bg/30 text-tg-hint text-center tabular text-body">
              {takeRail.asset ? displayAsset(takeRail.asset) : '—'}
            </div>
          </div>
        </Field>

        <Field label={`${legWho} gets`} hint={hintWords(lockedSide === 'give' && 'from chain', math.isDerived(giveRail.asset) && 'auto')}>
          {settleLater ? (
            <AssetPicker value={giveRail.asset} onChange={(a) => setGiveRail({ slug: '', asset: a })} options={ASSETS} />
          ) : accountsDown ? (
            <AccountsDown value={giveRail} />
          ) : (
          <RailPicker
            value={giveRail}
            onChange={setGiveRail}
            rows={rows}
            partySlug={partyExists ? partyKey : undefined}
            partyEntity={entity}
            // Every treasury account here too — see the note on the pay-in side.
            loading={treasury.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder="Pay-out account"
          />
          )}
          <div className="flex gap-2 mt-2">
            <input
              className={`input flex-1 min-w-0${math.isDerived(giveRail.asset) ? ' text-tg-hint' : ''}${lockedSide === 'give' ? ' bg-tg-bg/30' : ''}`}
              value={shownAmount(math.amountOf(giveRail.asset), giveRail.asset, math.isDerived(giveRail.asset), editing === 'give')}
              onChange={(e) => { if (lockedSide !== 'give') math.setAmount(giveRail.asset, e.target.value); }}
              onFocus={() => setEditing('give')}
              onBlur={() => setEditing(null)}
              readOnly={lockedSide === 'give'}
              inputMode="decimal"
              aria-label={`${legWho} gets, amount`}
            />
            <div className="w-24 shrink-0 input bg-tg-bg/30 text-tg-hint text-center tabular text-body">
              {giveRail.asset ? displayAsset(giveRail.asset) : '—'}
            </div>
          </div>
        </Field>
      </FieldRow>
      {/* Same account / same asset — under the legs it concerns (§5.1). */}
      <ErrorLine className="mt-0">{legsProblem}</ErrorLine>

      {/* Rate + When on one row (§7 rule 13). */}
      <FieldRow>
        {/* "AED per USDT" / "INR per AED" sits on the label so the operator reads
            which way the rate goes before typing it — "AED per USDT · auto" when
            the two amounts are the facts and the rate is the box that moves. A
            rate pasted from Telegram lands as typed — the calculator parses, the
            box does not. No placeholder: the hint already says the unit (§6). */}
        <Field label="Rate" hint={hintWords(!!math.rateWords && !sameAssetPair && math.rateWords, math.rateIsDerived && 'auto')}>
          <input
            className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
            value={math.rate}
            onChange={(e) => math.setRate(e.target.value)}
            inputMode="decimal"
            aria-label="Rate"
          />
          {/* The rate's shape (item 21), then the same rate read both ways
              (item 24 — the line Convert already has), then the band and the
              desk's own median (items 24, 25).

              21 Oct 2026 audit, two repeats removed. (1) The both-ways line is
              the check for a rate typed upside down; it now shows only when the
              rate is NOT plainly inside its band — outside it, in a pair with no
              band, or flagged by the desk's median. A rate inside the band is
              the right way up by definition, and the box's label already says
              which way it reads ("AED per USDT·TRC"). (2) A typed off-band rate
              printed its sentence here AND in the confirm panel above Book; the
              panel carries it (with the button) while it is open, so it stays
              here only when the panel is not — a worked-out rate, an answered
              confirm, or a hard stop still on. The median line says another
              thing (what the desk dealt today) and stays. */}
          <ErrorLine>{rateShape}</ErrorLine>
          {bothWays && (
            <p className="text-meta text-tg-hint mt-1">
              1 {displayAsset(math.base)} = {fmtRate(rateN, math.quote)} {displayAsset(math.quote)} · 1 {displayAsset(math.quote)} = {fmtRate(1 / rateN, math.base)} {displayAsset(math.base)}
            </p>
          )}
          <WarnLine>{bandLine && !(bandPending && !hardStop) && `⚠ ${bandLine}${math.rateIsDerived ? ' Worked out from the two amounts — check them.' : ''}`}</WarnLine>
          <WarnLine>{medianLine && `⚠ ${medianLine}`}</WarnLine>
          {/* The desk's average could not be read — or has not answered yet: stale figures stay suppressed, and it is said (the failure with
              its way out, the wait as a reading line), never read as "no average to compare with". */}
          {pairKnown && rateN > 0 && (medianRead === 'failed' ? (
            <StateLine tone="warn" className="mt-1" action={<button type="button" className="btn-link text-meta" onClick={() => void median.refetch()}>Retry</button>}>
              Desk average not read — try again.
            </StateLine>
          ) : medianRead === 'waiting' && (
            <StateLine className="mt-1">Reading the desk average…</StateLine>
          ))}
        </Field>
        {/* AS OF (5 Sept 2026 — the Loki 3 Sept trade; see the `when` state).
            It applies to both shapes: a settled trade dated to when the money
            really moved, or a settle-later one the sweep then matches with
            money recorded around that time. The chip row owns the picker and
            the "Books it as …" echo (14 Sept 2026); the screen keeps the value
            and its rules. */}
        <AsOfChips preset={urlWhen || undefined} label="When did this happen?" value={when} onChange={onWhenChange} disabled={submit.isPending} />
      </FieldRow>

      {/* The fold: Notes (typed on 6% of trades; the Chain prefill lands here). */}
      <MoreDetails label="More">
        <Field label="Notes">
          <textarea
            className="input h-16"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything to remember"
          />
        </Field>
      </MoreDetails>

      {/* ── WORDS BEFORE THE ROUND TRIP, and every answer after it, directly
          above the button (§5.1) ─────────────────────────────────────────── */}
      {/* Sides swapped (item 40): the BFF's own sentence, Book off, nothing
          flipped for the operator. */}
      <ErrorLine className="mt-0">{swapped}</ErrorLine>
      {/* The readback (item 14): the trade in words from the payload's state —
          the name `.who` once known (muted while the slug resolves), the two
          legs the answering figures. `readback` is the same sentence as a
          string; the success line prints that. */}
      {readback && !swapped && (
        <TradeReadback who={who} nameKnown={nameKnown} take={{ asset: takeRail.asset, amount: takeN }} give={{ asset: giveRail.asset, amount: giveN }} />
      )}
      {/* An unusual TYPED rate (item 24): Book is off; this button books. */}
      {bandPending && !hardStop && (
        <div className="rounded-lg bg-warn-bg p-3 text-body space-y-2">
          <p className="text-meta text-warn">{bandLine}</p>
          {/* Full width on the phone, content-sized at lg+ (phone pass, 19 Sept 2026). */}
          <button type="button" className="btn-secondary text-body py-1.5 text-warn w-full lg:w-auto" disabled={submit.isPending} onClick={confirmBand}>
            Book anyway — the rate is unusual
          </button>
        </div>
      )}
      {/* This is a conversion (item 27): Convert balance settles it now; or
          continue as settle-later and let the sweep net it. */}
      {convPending && !hardStop && (
        <div className="rounded-lg bg-warn-bg p-3 text-body space-y-2">
          <StateLine tone="warn" more="Convert balance settles it now. Continuing books it settle later, and the sweep nets it.">
            You already hold <span className="whitespace-nowrap">{fmtAmount(heldTake, takeRail.asset)} {foldAsset(takeRail.asset)}</span> for them — a conversion.
          </StateLine>
          {/* Two buttons that do not share a 375px line: a stack on the phone,
              one wrapping row at lg+ as before (phone pass, 19 Sept 2026). */}
          <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap">
            <Link to={`/parties/${encodeURIComponent(partyKey)}/convert`} className="btn-secondary text-body py-1.5 text-center w-full lg:w-auto">
              Convert balance →
            </Link>
            <button type="button" className="btn-secondary text-body py-1.5 text-warn w-full lg:w-auto" disabled={submit.isPending} onClick={confirmConversion}>
              Continue — book it settle later
            </button>
          </div>
        </div>
      )}

      {pendingProfileChoice && (
        <div className="border border-tg-accent/40 rounded p-3 space-y-2 bg-tg-section-bg">
          {/* The nearest existing names first (§5.1), then the create-as
              buttons in New party's words (item 78). */}
          {nearest.length > 0 && (
            <p className="text-meta text-tg-hint flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <span>Did you mean</span>
              {nearest.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className="btn-link text-meta"
                  disabled={submit.isPending}
                  onClick={() => { setPartySlug(o.value); setPendingProfileChoice(null); }}
                >
                  {o.label ?? o.value}
                </button>
              ))}
              <span>?</span>
            </p>
          )}
          <p className="text-body">
            <span className="font-semibold">{pendingProfileChoice.display_name}</span> doesn&rsquo;t exist yet — create as:
          </p>
          {/* ONE TAP, ONE CHOICE. These three were the only buttons on this
              screen still live while a booking was in flight — Book itself has
              been disabled on isPending all along. Creating IS the re-POST of
              the whole trade, so a second tap sent the trade again: the stable
              deal id means the ledger dedupes the trade, but the second request
              races the party creation and comes back as a refusal for a trade
              that in fact booked. Nothing reads worse than that. */}
          {/* ONE ROW OF THREE (21 Oct 2026 audit): three stacked buttons, two of
              them carrying a hint line, filled a phone's screen to say three words.
              The middle column is wider so "Treasury-only" stays on one line at
              390 px; the hints are a tooltip on the button and a line in About. */}
          <div className="grid grid-cols-[1fr_1.4fr_1fr] gap-1.5">
            <LazyChoiceButton
              label="Tracked"
              hint="KYC required"
              busy={onboarding?.kind === 'customer' && onboarding.profile_kind === 'tracked'}
              // Creating re-posts the trade, date and all — the same
              // back-date gate as Book (review, 5 Sept 2026).
              disabled={submit.isPending || !!whenWords}
              onClick={() => book({ kind: 'customer', profile_kind: 'tracked' })}
            />
            <LazyChoiceButton
              label="Treasury-only"
              hint="No KYC; name only"
              busy={onboarding?.kind === 'customer' && onboarding.profile_kind === 'treasury_only'}
              disabled={submit.isPending || !!whenWords}
              onClick={() => book({ kind: 'customer', profile_kind: 'treasury_only' })}
            />
            <LazyChoiceButton
              label="Merchant"
              busy={onboarding?.kind === 'merchant'}
              disabled={submit.isPending || !!whenWords}
              onClick={() => book({ kind: 'merchant' })}
            />
          </div>
          <button
            type="button"
            className="btn-link text-meta text-tg-hint disabled:opacity-40"
            disabled={submit.isPending}
            onClick={() => setPendingProfileChoice(null)}
          >
            Cancel
          </button>
        </div>
      )}

      {/* The new-account door, in words (see `newRail`). Saying yes re-posts
          the same trade, same deal id, with allow_new_rail_asset — remembered
          with the other yeses (confirmedRef), so a later "book it again
          anyway" does not meet this door a second time. */}
      {newRail && (
        <div className="rounded-lg bg-warn-bg p-3 space-y-2">
          <StateLine tone="warn">
            That account has never held {displayAsset(newRail.asset)}.
          </StateLine>
          <p className="text-meta text-tg-hint">
            {newRail.holds.length
              ? `Holds ${newRail.holds.map(displayAsset).join(' / ')} today`
              : 'Never posted to'}
            {' — check asset and account first.'}
          </p>
          <button
            type="button"
            // A long label: full width on the phone so it wraps inside its own
            // box, content-sized at lg+ as before (phone pass, 19 Sept 2026).
            className="btn-secondary text-body w-full lg:w-auto"
            disabled={submit.isPending || !!whenWords}
            onClick={() => { confirmedRef.current.add('new_rail'); book(submit.variables); }}
          >
            Yes — open {displayAsset(newRail.asset)} on {railName({ slug: newRail.rail.slug })} and book
          </button>
        </div>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          // A back-date the BFF would refuse locks this door as well.
          pending={submit.isPending || !!whenWords || !!conversionGate(laterFor(submit.variables))}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // Confirm the guard that refused, and only that one; an earlier
          // confirmation stays in the set, so a trade refused twice in turn
          // goes out with both flags (see confirmedRef). The re-POST carries the
          // shape of the first attempt: the gate reads it from the variables and
          // refuses BEFORE the confirm is remembered.
          onPostAnyway={() => {
            if (conversionGate(laterFor(submit.variables))) return;
            confirmedRef.current.add(repeat.kind);
            book(submit.variables);
          }}
          // "Book it settle-later instead" (sweep item 39): the honest answer
          // to money_already_recorded is usually the trade booked settle-later,
          // so the entry already in the books settles it. This screen has the
          // switch, so the remedy flips it and re-posts the same trade — the
          // shape rides in the variables (SubmitVars) because the re-POST
          // reads this render's closure, where the switch still says settled.
          // Only where settle-later is on offer at all: merchants, and anyone
          // already carrying a position (canSettleLater).
          settleLater={canSettleLater
            // The remedy flips the switch and books settle-later; with the conversion check open it only flips the switch
            // (the question or the failed read then shows) and `book` holds the trade back.
            ? { onClick: () => { laterTouched.current = true; setSettleLater(true); book({ kind: 'settle_later' }); } }
            : undefined}
        />
      )}
      {submit.isError && !isSentinel(submit.error) && !newRail && (
        <ErrorLine className="mt-0">{errorWords(submit.error)}</ErrorLine>
      )}

      {/* A back-dated booking says its date back, so "✓" alone can never hide
          a trade that landed on the wrong day — read from what was POSTED
          (`booked`), never from the live field. A deduped answer says so
          instead: the books already held this id and kept the first date. */}
      {submit.isSuccess && booked && <BookedLine booked={booked} />}

      {/* INR trades only — see TradeChargeLinks. */}
      {submit.isSuccess && booked && !booked.deduped && booked.inr && (
        <TradeChargeLinks dealId={booked.dealId} partySlug={booked.partySlug} />
      )}

      {/* The dating line (see `dating`): this party's box already moved one
          of these legs, more than 12 h ago, while the trade says Now. The tap
          dates the trade to that movement through the same door the chips
          use (onWhenChange), so the deal id and the confirms follow. */}
      {datingWanted && (recent === 'failed' ? (
        <StateLine tone="warn" action={<button type="button" className="btn-link text-meta" onClick={() => void recentMoney.refetch()}>Retry</button>}>
          Recent movements not read — try again.
        </StateLine>
      ) : recent === 'waiting' && (
        <StateLine>Reading recent movements…</StateLine>
      ))}
      {dating && (
        <StateLine
          tone="warn"
          more={`${dating.reference ? `${dating.reference}. ` : ''}The sweep matches money to a trade by date: date the trade to when the money moved.`}
          action={
            <button
              type="button"
              className="btn-link text-meta"
              disabled={submit.isPending}
              onClick={() => onWhenChange(isoToDubaiWall(dating.at))}
            >
              Set to then
            </button>
          }
        >
          Money moved {dating.hours} h ago — date the trade to then?
        </StateLine>
      )}

      {/* lg+: hairline separator + right-aligned, content-sized submit.
          21 Oct 2026 audit: while a repeat / twin panel is open, Book trade
          drops to the secondary style — the panel's own remedy ("Book it
          settle-later instead") is the one gold button, and a plain repeat
          has none: the question above is answered before the screen asks again.
          A restyle only: nothing is disabled, the button books exactly as before. */}
      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className={`${repeat ? 'btn-secondary' : 'btn-primary'} w-full lg:w-auto lg:px-6`}
          disabled={bookDisabled}
          onClick={() => book(undefined)}
        >
          {/* The button names the shape (§6 settle-later) and the date, so the
              operator sees what they are committing to at the moment they
              commit (review, 5 Sept 2026). After a booking the figures are
              gone and the button reads "Book another" (item 14). */}
          {submit.isPending ? 'Booking…'
            : bookedAndCleared ? 'Book another'
            // The only thing between the operator and the button is a box nobody could read: say so on the button.
            : conversionUnchecked && !hardStop && !bandPending ? uncheckedWords
            : `Book trade${settleLater ? ' · settle later' : ''}${whenIso && !whenWords ? ` · as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>

      {/* THE LAST TEN TRADES OF THIS TYPE, newest first (operator, 16 Oct
          2026: "Newest first" — until then oldest first, by his 17 Sept
          "in ascending order") — the direction on
          the toggle, the pair once both legs are known, the chosen party
          once one is picked. Under the form, on its own read; it never holds
          the button. */}
      {/* Last 10 reads the party's deals under the SAME key as the balance read above; when that read failed the banner
          has said so, and RecentEntries would say it again — in the books' POST sentence ("Check the trades log before
          posting this again") under a read. One message: the list waits for the read. */}
      {!(partyExists && box === 'failed') && <RecentEntries
        kind="trades"
        direction={direction}
        pair={pairKnown ? { base: math.base, quote: math.quote } : undefined}
        partySlug={partyExists ? partyKey : undefined}
        title={`Last 10 ${direction === 'customer_buy' ? 'buys' : 'sells'}`
          + (pairKnown ? ` of ${displayAsset(foldAsset(math.base))}` : '')
          + (partyExists ? ` with ${partyWords}` : '')}
      />}
    </div>
  );
}

/** THE TWO LINKS UNDER AN INR TRADE'S SUCCESS LINE (operator, 4 Oct 2026: an
 *  INR trade nearly always carries a charge). What the desk pays the angadiya
 *  opens Record expense with the category and a note naming the deal; what it
 *  takes from the customer opens the delivery-charge form with the party and
 *  the deal — the same link the deal panel carries. Each is its own entry,
 *  never folded into the rate, which would blur the spread and hide the cost
 *  from Profit. The account that paid is left for the operator to name: only
 *  they know which pocket the money came from. */
export function TradeChargeLinks({ dealId, partySlug }: { dealId: string; partySlug: string }) {
  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-body lg:text-meta">
      <Link
        to={`/record/expense?category=angadiya&note=${encodeURIComponent(`angadiya commission · ${dealId}`)}`}
        className="btn-link text-meta"
      >
        Record the cost →
      </Link>
      <Link
        to={`/record?mode=charge&party=${encodeURIComponent(partySlug)}&deal=${encodeURIComponent(dealId)}`}
        className="btn-link text-meta"
      >
        Add delivery charge →
      </Link>
    </p>
  );
}

/** The BFF's "this rail has never held this asset" refusal (400 new_rail_asset). */
function isNewRailRefusal(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'new_rail_asset';
}
/** What that rail holds today, from the refusal's own body. */
function newRailHolds(e: unknown): string[] {
  const holds = (e as ApiError)?.body?.holds;
  return Array.isArray(holds) ? holds.map(String) : [];
}

/** One create-as path for a party the books have never seen — New party's
 *  words (item 78). Pressing it re-posts the whole trade, so it locks like
 *  the Book button does and says "Booking…" while it works.
 *  21 Oct 2026 audit: the hint ("KYC required", "No KYC; name only") no longer
 *  prints under the label — it is the button's tooltip, and About says it. */
function LazyChoiceButton({ label, hint, busy, disabled, onClick }: {
  label: string;
  hint?: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="btn-secondary text-body py-2 px-2 text-center disabled:opacity-40"
      disabled={disabled}
      title={hint}
      onClick={onClick}
    >
      <span className="font-medium">{busy ? 'Booking…' : label}</span>
    </button>
  );
}

// The settle-later leg's asset control used to be a local <select> over
// ASSETS ("pick asset"); it is the shared AssetPicker now (12 Sept 2026) —
// asset without a drawer is still the only honest control for an unsettled leg.
// The local Field went the same way on 14 Sept 2026 (components/Field.tsx).
